import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Alert,
  IconButton,
  Checkbox,
} from '@mui/material';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import CloseIcon from '@mui/icons-material/Close';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { getHistory, HistoryEntry } from '../../utils/historyStorage';
import { exportComparisonToExcel } from '../../utils/exportUtils';
import {
  RESULT_ALGORITHM_NAMES,
  getResultAlgorithmKeysForResultsList,
} from '../../utils/algorithmRegistry';

interface ComparisonViewProps {
  open: boolean;
  onClose: () => void;
  onError?: (message: string) => void;
}

const ComparisonView: React.FC<ComparisonViewProps> = ({ open, onClose, onError }) => {
  const [selectedEntries, setSelectedEntries] = useState<string[]>([]);
  const history = getHistory();

  const handleToggleEntry = (id: string) => {
    setSelectedEntries(prev => {
      if (prev.includes(id)) {
        return prev.filter(eid => eid !== id);
      }
      if (prev.length < 3) { // Max 3 comparisons
        return [...prev, id];
      }
      return prev;
    });
  };

  const handleClearSelection = () => {
    setSelectedEntries([]);
  };

  const comparisonEntries = useMemo(() => {
    return selectedEntries
      .map(id => history.find(entry => entry.id === id))
      .filter((entry): entry is HistoryEntry => entry !== undefined);
  }, [selectedEntries, history]);

  const algorithmKeys = useMemo(
    () => getResultAlgorithmKeysForResultsList(comparisonEntries.map((entry) => entry.results)),
    [comparisonEntries]
  );
  const algorithms = useMemo(
    () => algorithmKeys.map((key) => RESULT_ALGORITHM_NAMES[key]),
    [algorithmKeys]
  );
  const hasSflaGaBaseline = useMemo(
    () =>
      comparisonEntries.some(
        (entry) => entry.results.configuration.includeSflaGaBaseline ?? Boolean(entry.results.sflaGa)
      ),
    [comparisonEntries]
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CompareArrowsIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">Compare Results</Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {history.length === 0 ? (
          <Alert severity="info">No results available for comparison. Run optimizations first.</Alert>
        ) : (
          <Box>
            {/* Selection Area */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  Select Results to Compare (up to 3)
                </Typography>
                {selectedEntries.length > 0 && (
                  <Button size="small" onClick={handleClearSelection}>
                    Clear Selection
                  </Button>
                )}
              </Box>
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 200, overflow: 'auto' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" />
                      <TableCell><strong>Timestamp</strong></TableCell>
                      <TableCell align="right"><strong>Tasks</strong></TableCell>
                      <TableCell align="right"><strong>Population</strong></TableCell>
                      <TableCell align="right"><strong>Iterations</strong></TableCell>
                      <TableCell align="right"><strong>Runs</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {history.map((entry) => (
                      <TableRow
                        key={entry.id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => handleToggleEntry(entry.id)}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedEntries.includes(entry.id)}
                            onChange={() => handleToggleEntry(entry.id)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </TableCell>
                        <TableCell>{new Date(entry.timestamp).toLocaleString()}</TableCell>
                        <TableCell align="right">{entry.results.configuration.taskCount}</TableCell>
                        <TableCell align="right">{entry.results.configuration.initialPopulation}</TableCell>
                        <TableCell align="right">{entry.results.configuration.iterations ?? '-'}</TableCell>
                        <TableCell align="right">
                          {entry.results.runNumber ? (
                            <Chip label={entry.results.runNumber} size="small" color="primary" />
                          ) : (
                            '-'
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            {/* Comparison Tables */}
            {comparisonEntries.length > 0 && (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Comparison Results
                  </Typography>
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    startIcon={<FileDownloadIcon />}
                    onClick={() => {
                      try {
                        const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
                        const filename = `comparison_results_${timestamp}.xlsx`;
                        exportComparisonToExcel(comparisonEntries, filename);
                        if (onError) {
                          // Success - could add success notification if needed
                        }
                      } catch (error) {
                        const errorMessage = 'Failed to export comparison: ' + (error instanceof Error ? error.message : String(error));
                        if (onError) {
                          onError(errorMessage);
                        } else {
                          alert(errorMessage); // Fallback if no error handler
                        }
                      }
                    }}
                  >
                    Export Comparison
                  </Button>
                </Box>

                {/* Fitness Comparison */}
                <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                  <Box sx={{ p: 2, bgcolor: 'primary.main', color: 'white' }}>
                    <Typography variant="h6">Fitness Comparison</Typography>
                  </Box>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Algorithm</strong></TableCell>
                        {comparisonEntries.map((entry, idx) => (
                          <TableCell key={entry.id} align="right">
                            <Typography variant="body2" fontWeight={600}>
                              Run {idx + 1}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {new Date(entry.timestamp).toLocaleDateString()}
                            </Typography>
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {algorithmKeys.map((key, idx) => {
                        // Find best (highest) and worst (lowest) fitness values across all entries
                        const fitnessValues = comparisonEntries
                          .map(entry => entry.results[key]?.fitness)
                          .filter((value): value is number => Number.isFinite(value));
                        const maxFitness = Math.max(...fitnessValues);
                        const minFitness = Math.min(...fitnessValues);
                        
                        return (
                          <TableRow key={key}>
                            <TableCell><strong>{algorithms[idx]}</strong></TableCell>
                            {comparisonEntries.map((entry) => {
                              const fitness = entry.results[key]?.fitness;
                              if (fitness === undefined || !Number.isFinite(fitness)) {
                                return (
                                  <TableCell key={entry.id} align="right">
                                    -
                                  </TableCell>
                                );
                              }
                              const isBest = fitness === maxFitness && fitnessValues.length > 1;
                              const isWorst = fitness === minFitness && fitnessValues.length > 1 && maxFitness !== minFitness;
                              
                              return (
                                <TableCell
                                  key={entry.id}
                                  align="right"
                                  sx={{
                                    bgcolor: isBest ? 'success.light' : isWorst ? 'error.light' : 'transparent',
                                    fontWeight: isBest || isWorst ? 600 : 400,
                                  }}
                                >
                                  {fitness.toFixed(6)}
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Energy Comparison */}
                <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                  <Box sx={{ p: 2, bgcolor: 'primary.main', color: 'white' }}>
                    <Typography variant="h6">Energy Comparison (Joules)</Typography>
                  </Box>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Algorithm</strong></TableCell>
                        {comparisonEntries.map((entry, idx) => (
                          <TableCell key={entry.id} align="right">
                            <Typography variant="body2" fontWeight={600}>
                              Run {idx + 1}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {new Date(entry.timestamp).toLocaleDateString()}
                            </Typography>
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {algorithmKeys.map((key, idx) => {
                        // Find best (lowest) and worst (highest) energy values across all entries
                        const energyValues = comparisonEntries
                          .map(entry => entry.results[key]?.energy)
                          .filter((value): value is number => Number.isFinite(value));
                        const minEnergy = Math.min(...energyValues);
                        const maxEnergy = Math.max(...energyValues);
                        
                        return (
                          <TableRow key={key}>
                            <TableCell><strong>{algorithms[idx]}</strong></TableCell>
                            {comparisonEntries.map((entry) => {
                              const energy = entry.results[key]?.energy;
                              if (energy === undefined || !Number.isFinite(energy)) {
                                return (
                                  <TableCell key={entry.id} align="right">
                                    -
                                  </TableCell>
                                );
                              }
                              const isBest = energy === minEnergy && energyValues.length > 1;
                              const isWorst = energy === maxEnergy && energyValues.length > 1 && minEnergy !== maxEnergy;
                              
                              return (
                                <TableCell
                                  key={entry.id}
                                  align="right"
                                  sx={{
                                    bgcolor: isBest ? 'success.light' : isWorst ? 'error.light' : 'transparent',
                                    fontWeight: isBest || isWorst ? 600 : 400,
                                  }}
                                >
                                  {energy.toFixed(2)} J
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Response Time Comparison */}
                <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                  <Box sx={{ p: 2, bgcolor: 'primary.main', color: 'white' }}>
                    <Typography variant="h6">Response Time Comparison (ms)</Typography>
                  </Box>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Algorithm</strong></TableCell>
                        {comparisonEntries.map((entry, idx) => (
                          <TableCell key={entry.id} align="right">
                            <Typography variant="body2" fontWeight={600}>
                              Run {idx + 1}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {new Date(entry.timestamp).toLocaleDateString()}
                            </Typography>
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {algorithmKeys.map((key, idx) => {
                        // Find best (lowest) and worst (highest) response time values across all entries
                        const responseTimeValues = comparisonEntries
                          .map(entry => entry.results[key]?.responseTime)
                          .filter((value): value is number => Number.isFinite(value));
                        const minResponseTime = Math.min(...responseTimeValues);
                        const maxResponseTime = Math.max(...responseTimeValues);
                        
                        return (
                          <TableRow key={key}>
                            <TableCell><strong>{algorithms[idx]}</strong></TableCell>
                            {comparisonEntries.map((entry) => {
                              const responseTime = entry.results[key]?.responseTime;
                              if (responseTime === undefined || !Number.isFinite(responseTime)) {
                                return (
                                  <TableCell key={entry.id} align="right">
                                    -
                                  </TableCell>
                                );
                              }
                              const isBest = responseTime === minResponseTime && responseTimeValues.length > 1;
                              const isWorst = responseTime === maxResponseTime && responseTimeValues.length > 1 && minResponseTime !== maxResponseTime;
                              
                              return (
                                <TableCell
                                  key={entry.id}
                                  align="right"
                                  sx={{
                                    bgcolor: isBest ? 'success.light' : isWorst ? 'error.light' : 'transparent',
                                    fontWeight: isBest || isWorst ? 600 : 400,
                                  }}
                                >
                                  {responseTime.toFixed(2)} ms
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Configuration Comparison */}
                <TableContainer component={Paper} variant="outlined">
                  <Box sx={{ p: 2, bgcolor: 'primary.main', color: 'white' }}>
                    <Typography variant="h6">Configuration Comparison</Typography>
                  </Box>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Parameter</strong></TableCell>
                        {comparisonEntries.map((entry, idx) => (
                          <TableCell key={entry.id} align="right">
                            <Typography variant="body2" fontWeight={600}>
                              Run {idx + 1}
                            </Typography>
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>Tasks</TableCell>
                        {comparisonEntries.map((entry) => (
                          <TableCell key={entry.id} align="right">
                            {entry.results.configuration.taskCount}
                          </TableCell>
                        ))}
                      </TableRow>
                      <TableRow>
                        <TableCell>Population</TableCell>
                        {comparisonEntries.map((entry) => (
                          <TableCell key={entry.id} align="right">
                            {entry.results.configuration.initialPopulation}
                          </TableCell>
                        ))}
                      </TableRow>
                      <TableRow>
                        <TableCell>Iterations</TableCell>
                        {comparisonEntries.map((entry) => (
                          <TableCell key={entry.id} align="right">
                            {entry.results.configuration.iterations ?? '-'}
                          </TableCell>
                        ))}
                      </TableRow>
                      <TableRow>
                        <TableCell>Selection Rate</TableCell>
                        {comparisonEntries.map((entry) => (
                          <TableCell key={entry.id} align="right">
                            {entry.results.configuration.selectionRate !== undefined ? `${entry.results.configuration.selectionRate}%` : '-'}
                          </TableCell>
                        ))}
                      </TableRow>
                      <TableRow>
                        <TableCell>Crossover Rate</TableCell>
                        {comparisonEntries.map((entry) => (
                          <TableCell key={entry.id} align="right">
                            {entry.results.configuration.crossover}%
                          </TableCell>
                        ))}
                      </TableRow>
                      <TableRow>
                        <TableCell>Mutation Rate</TableCell>
                        {comparisonEntries.map((entry) => (
                          <TableCell key={entry.id} align="right">
                            {entry.results.configuration.mutation}%
                          </TableCell>
                        ))}
                      </TableRow>
                      <TableRow>
                        <TableCell>Memeplex Count</TableCell>
                        {comparisonEntries.map((entry) => (
                          <TableCell key={entry.id} align="right">
                            {entry.results.configuration.memeplexCount ?? '-'}
                          </TableCell>
                        ))}
                      </TableRow>
                      <TableRow>
                        <TableCell>Parameter Policy</TableCell>
                        {comparisonEntries.map((entry) => (
                          <TableCell key={entry.id} align="right">
                            {entry.results.configuration.parameterPolicy ?? '-'}
                          </TableCell>
                        ))}
                      </TableRow>
                      <TableRow>
                        <TableCell>Number of Runs</TableCell>
                        {comparisonEntries.map((entry) => (
                          <TableCell key={entry.id} align="right">
                            {entry.results.runNumber || 1}
                          </TableCell>
                        ))}
                      </TableRow>
                      {hasSflaGaBaseline && (
                        <TableRow>
                          <TableCell>SFLA-GA Baseline</TableCell>
                          {comparisonEntries.map((entry) => (
                            <TableCell key={entry.id} align="right">
                              {(entry.results.configuration.includeSflaGaBaseline ?? Boolean(entry.results.sflaGa)) ? 'Included' : 'Excluded'}
                            </TableCell>
                          ))}
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ComparisonView;
